import React, {useCallback, useEffect, useRef, useState} from 'react';

/**
 * Kurztext mit aufklappbarem Langtext für Sprechblasen (Ticket #127, Teil 4).
 *
 * Die Sprechblase zeigt zuerst nur den Kurztext. Steht ein Langtext dahinter,
 * klappt „mehr" ihn auf. Damit man „mehr" erreichen kann, bleibt die
 * Sprechblase offen, solange der Mauszeiger auf ihr liegt.
 *
 * @param {React.ReactNode} tip - Kurztext, immer sichtbar.
 * @param {React.ReactNode} [detail] - Langtext, erst nach Klick auf „mehr".
 * @param {boolean} open - Langtext sichtbar?
 * @param {Function} onOpen - Klick auf „mehr".
 */
export function TipBody({tip, detail, open, onOpen}) {
    if (!detail) {
        return tip;
    }

    return (
        <>
            {tip}
            {open
                ? <div className="tip-detail">{detail}</div>
                : (
                    <>
                        {' '}
                        <button
                            type="button"
                            className="tip-more"
                            onClick={(e) => { e.stopPropagation(); onOpen(); }}
                        >
                            mehr
                        </button>
                    </>
                )}
        </>
    );
}

/**
 * Steuert eine Sprechblase so, dass sie offen bleibt, solange der Mauszeiger
 * auf ihr liegt. Ohne Langtext verhält sie sich wie bisher.
 *
 * @param {boolean} sticky - Nur mit Langtext nötig.
 * @returns {{show: boolean, open: boolean, setOpen: Function, onToggle: Function, tipProps: object}}
 */
export function useStickyTip(sticky) {
    const [show, setShow] = useState(false);
    const [open, setOpen] = useState(false);
    const overTip = useRef(false);
    const timer = useRef(null);

    useEffect(() => () => clearTimeout(timer.current), []);

    const close = useCallback(() => {
        setShow(false);
        setOpen(false);
    }, []);

    const onToggle = useCallback((next) => {
        clearTimeout(timer.current);
        if (next) {
            setShow(true);
            return;
        }
        // Kurz warten: Der Weg vom Element zur Sprechblase verlässt das Element.
        timer.current = setTimeout(() => {
            if (!overTip.current) {
                close();
            }
        }, 200);
    }, [close]);

    const tipProps = sticky ? {
        className: 'tip-sticky',
        onMouseEnter: () => {
            overTip.current = true;
            clearTimeout(timer.current);
        },
        onMouseLeave: () => {
            overTip.current = false;
            timer.current = setTimeout(close, 150);
        },
    } : {};

    return {show, open, setOpen, onToggle, tipProps};
}
